import { UseFormReset } from "react-hook-form";
import { CONFIG_PAGES, HTTP_STATUSES } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { IOrderTemplateResponse } from "@miplatform/types";
import { addSeconds, hoursToMinutes, minutesToHours } from "@miplatform/utils";
import { useEmailTemplate, useOrderTemplate } from "@mibuildweb/hooks";
import { CONFIG_PATHS } from "../../../../routes/config/constants";
import {
  IEmailTemplateData,
  IEmailTemplateForm,
  IOrderTemplateForm,
  IOrderTemplatePayload,
} from "../ConfigOrderTemplates.types";

const mapAssignmentTemplateToOrderTemplate = (
  orderTemplate: IOrderTemplateResponse
) => {
  if (
    !orderTemplate.assignmentTemplates ||
    orderTemplate.assignmentTemplates.length === 0
  ) {
    return orderTemplate;
  }

  const generatedAssignmentTemplate =
    orderTemplate.assignmentTemplates.length === 1
      ? orderTemplate.assignmentTemplates[0]
      : null;

  return {
    ...orderTemplate,
    ...(generatedAssignmentTemplate && {
      scheduleTimeThreshold: generatedAssignmentTemplate.scheduleTimeThreshold,
      allowableScheduleLimit:
        generatedAssignmentTemplate.allowableScheduleLimit,
    }),
  };
};

export const useOrderTemplateDetailsViewModel = ({
  reset,
  emailReset,
  isOrderTemplateDirty,
  isEmailTemplateDirty,
}: IUseOrderTemplateDetailsViewModel) => {
  const { navigate } = useRouter();

  const {
    orderTemplateDetails,
    getOrderTemplateDetails,
    createOrderTemplate,
    updateOrderTemplate,
    isLoading,
  } = useOrderTemplate();

  const { getEmailTemplatesByOrderTemplateId, updateEmailTemplates } =
    useEmailTemplate();

  const updateDraftStatus = (orderTemplateId: string, isActive: boolean) => {
    updateOrderTemplate(
      orderTemplateId,
      { isActive },
      {
        onSuccess: reset,
        name: orderTemplateDetails?.name,
      }
    );
  };

  const handleCreateOrderTemplate = async (
    data: IOrderTemplateForm,
    emailData: IEmailTemplateForm
  ) => {
    const { expectedDurationInHours, ...orderTemplatePayload } = data;
    const modifiedData: IOrderTemplatePayload = {
      ...orderTemplatePayload,
      departments: data.departments?.map((d) => d.id),
      approvalLeadTime: addSeconds(data.approvalLeadTime),
      confirmationLeadTime: addSeconds(data.confirmationLeadTime),
      expectedDurationInMinutes: hoursToMinutes(expectedDurationInHours),
    };

    const response = await createOrderTemplate(modifiedData);

    if (response.status === HTTP_STATUSES.OK) {
      if (isEmailTemplateDirty) {
        await updateEmailTemplates(emailData, response?.data.id, {
          onSuccess: (templates) => emailReset({ emailTemplates: templates }),
        });
      }
      navigate(CONFIG_PATHS[CONFIG_PAGES.ORDER_TEMPLATES]);
    }
  };

  const handleGetOrderTemplateDetails = async (id: string) => {
    const response = await getOrderTemplateDetails(id);
    if (response.status === HTTP_STATUSES.OK) {
      const { data } = response;
      const modifiedResponseData = mapAssignmentTemplateToOrderTemplate({
        ...data,
        expectedDurationInHours: minutesToHours(data.expectedDurationInMinutes),
      });
      reset(modifiedResponseData);
    }
  };

  const handleUpdateOrderTemplate = async (
    data: Partial<IOrderTemplateForm>,
    emailData: IEmailTemplateForm,
    orderTemplateId: string
  ) => {
    if (isOrderTemplateDirty) {
      const { expectedDurationInHours, departments, ...orderTemplatePayload } =
        data;
      const departmentIds =
        departments?.map((d) => d.id) ??
        orderTemplateDetails?.departments.map((d) => d.id) ??
        [];

      // prevent overwriting these fields if they are not updated
      const modifiedData: Partial<IOrderTemplatePayload> = {
        ...orderTemplatePayload,
        ...(departmentIds && {
          departments: departmentIds,
        }),
        ...(expectedDurationInHours !== undefined && {
          expectedDurationInMinutes: hoursToMinutes(expectedDurationInHours),
        }),
        ...(data.assignmentTemplates
          ? { assignmentTemplateId: data.assignmentTemplates[0]?.id }
          : (data.scheduleTimeThreshold || data.allowableScheduleLimit) && {
              assignmentTemplateId:
                orderTemplateDetails?.assignmentTemplates?.[0]?.id,
            }),
        ...(data.approvalLeadTime !== undefined && {
          approvalLeadTime: addSeconds(data.approvalLeadTime),
        }),
        ...(data.confirmationLeadTime !== undefined && {
          confirmationLeadTime: addSeconds(data.confirmationLeadTime),
        }),
      };

      const { data: updatedData } = await updateOrderTemplate(
        orderTemplateId,
        modifiedData,
        { name: orderTemplateDetails?.name }
      );
      const modifiedResponseData = mapAssignmentTemplateToOrderTemplate({
        ...updatedData,
        expectedDurationInHours: minutesToHours(
          updatedData.expectedDurationInMinutes
        ),
        maxTimesPutOnHold: updatedData.maxTimesPutOnHold ?? null,
        fulfillmentSequence: updatedData.fulfillmentSequence ?? null,
      });
      reset(modifiedResponseData);
    }
    if (isEmailTemplateDirty) {
      updateEmailTemplates(emailData, orderTemplateId, {
        onSuccess: (templates) => emailReset({ emailTemplates: templates }),
      });
    }
  };

  return {
    isLoading,
    getOrderTemplateDetails: handleGetOrderTemplateDetails,
    getEmailTemplatesByOrderTemplateId,
    handleCreateOrderTemplate,
    handleUpdateOrderTemplate,
    updateDraftStatus,
    orderTemplateDetails,
  };
};

interface IUseOrderTemplateDetailsViewModel {
  reset: UseFormReset<IOrderTemplateForm>;
  emailReset: UseFormReset<IEmailTemplateData>;
  isOrderTemplateDirty: boolean;
  isEmailTemplateDirty: boolean;
}
